import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, MailCheck, Shield } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useForm } from 'react-hook-form';
import { api } from '../../../lib/api';
import { toast } from 'sonner';

// Hinweis: Der Wartungsmodus wird NICHT hier gepflegt, sondern zentral unter
// „Kommunikation → Wartungsmodus (zentral)". Der Versand liest ihn von dort
// (Overlay im Backend), daher hat dieses Formular kein Wartungsmodus-Feld mehr.
export type EmailSettings = {
  transport: 'smtp';
  host: string;
  port: number | string;
  encryption: 'none' | 'ssl' | 'tls';
  username: string;
  password: string; // masked as *** on GET
  fromAddress: string;
  fromName: string;
  replyTo: string | null;
  timeout: number | string;
};

export default function EmailSettingsForm() {
  const [loading, setLoading] = useState(false);
  const form = useForm<EmailSettings>({
    defaultValues: {
      transport: 'smtp',
      host: '',
      port: 587,
      encryption: 'tls',
      username: '',
      password: '',
      fromAddress: '',
      fromName: 'SIDIGO',
      replyTo: '',
      timeout: 10,
    },
  });

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await api.get<EmailSettings>('/api/settings/email');
      // Wartungsmodus-Felder werden hier bewusst ignoriert (zentral gepflegt).
      const { maintenanceModeEnabled: _ignoredMme, maintenanceRedirectAddress: _ignoredMra, ...rest } =
        data as EmailSettings & { maintenanceModeEnabled?: boolean; maintenanceRedirectAddress?: string };
      const normalized = {
        ...rest,
        replyTo: (rest.replyTo ?? '') as any,
      };
      form.reset(normalized);
    } catch (e: any) {
      toast.error(e.message || 'E-Mail-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (values: EmailSettings) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        port: typeof values.port === 'string' ? parseInt(values.port, 10) : values.port,
        timeout: typeof values.timeout === 'string' ? parseInt(values.timeout, 10) : values.timeout,
        replyTo: values.replyTo && values.replyTo.trim() !== '' ? values.replyTo : null,
      };
      const saved = await api.put<EmailSettings>('/api/settings/email', payload);
      const normalized = { ...saved, replyTo: (saved.replyTo ?? '') as any };
      form.reset(normalized);
      toast.success('E-Mail-Einstellungen gespeichert');
    } catch (e: any) {
      toast.error(e.message || 'Speichern fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const [testTo, setTestTo] = useState('');
  const onTest = async () => {
    setLoading(true);
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(
        '/api/settings/email/test',
        testTo && testTo.trim() !== '' ? { to: testTo.trim() } : {}
      );
      if ((res as any).ok) {
        toast.success(testTo && testTo.trim() !== '' ? 'Test-E-Mail wurde versendet' : 'SMTP-Konfiguration erfolgreich getestet');
      } else {
        toast.error((res as any).error || 'SMTP-Test fehlgeschlagen');
      }
    } catch (e: any) {
      toast.error(e.message || 'SMTP-Test fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="mr-2 h-5 w-5" />
              SMTP-Verbindung
            </CardTitle>
            <CardDescription>Server, Port, Verschlüsselung und Zugangsdaten</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="transport"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transport</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Transport" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="smtp">SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="host"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="smtp.example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Port</FormLabel>
                    <FormControl>
                      <Input type="number" {...(field as any)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="encryption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verschlüsselung</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Verschlüsselung" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Keine</SelectItem>
                        <SelectItem value="ssl">SSL</SelectItem>
                        <SelectItem value="tls">TLS</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Benutzername</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Passwort</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} placeholder="***" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MailCheck className="mr-2 h-5 w-5" />
              Absender & Zeitlimit
            </CardTitle>
            <CardDescription>From/Reply-To und Timeout</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="fromAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Adresse</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="noreply@example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fromName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="SIDIGO" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="replyTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reply-To (optional)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="support@example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="timeout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Timeout (Sekunden)</FormLabel>
                    <FormControl>
                      <Input type="number" {...(field as any)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-end">
                <Input
                  placeholder="Test-Empfänger (optional)"
                  value={testTo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestTo(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" onClick={onTest} disabled={loading}>
                  {loading ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Test läuft...
                    </>
                  ) : (
                    <>
                      <MailCheck className="mr-2 h-4 w-4" />
                      {testTo && testTo.trim() !== '' ? 'Testmail senden' : 'Verbindung testen'}
                    </>
                  )}
                </Button>
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Einstellungen speichern
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}


